import React from 'react';
import { useAuth } from '../context/AuthContext';
import { CompanySettingsPage } from './CompanySettingsPage';
import { CandidateSettingsPage } from './CandidateSettingsPage';

// Skeleton layout for initial auth loading - shows a clean skeleton that works for any user type
const SettingsSkeleton = () => (
    <div className="dashboard-layout">
        {/* Skeleton Sidebar - Desktop Only */}
        <aside className="hidden lg:flex fixed left-0 top-0 h-full w-72 bg-white border-r border-slate-200 p-6 z-50 flex-col">
            {/* Logo skeleton */}
            <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 bg-slate-200 rounded-xl animate-pulse"></div>
                <div className="h-6 w-24 bg-slate-200 rounded animate-pulse"></div>
            </div>
            {/* Nav items skeleton */}
            <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl animate-pulse">
                        <div className="w-5 h-5 bg-slate-200 rounded"></div>
                        <div className="h-4 w-24 bg-slate-200 rounded"></div>
                    </div>
                ))}
            </div>
        </aside>

        {/* Mobile Header Skeleton */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2 animate-pulse">
                <div className="h-7 w-7 bg-slate-200 rounded"></div>
                <div className="h-5 w-20 bg-slate-200 rounded"></div>
            </div>
            <div className="w-6 h-6 bg-slate-200 rounded animate-pulse"></div>
        </div>

        {/* Main content skeleton */}
        <div className="main-content">
            <div className="max-w-7xl mx-auto">
                {/* Header skeleton */}
                <div className="mb-6 md:mb-10 animate-pulse">
                    <div className="h-7 md:h-9 bg-slate-200 rounded-lg w-28 md:w-32 mb-3"></div>
                    <div className="h-4 md:h-5 bg-slate-200 rounded w-64 md:w-80"></div>
                </div>

                {/* Settings cards skeleton */}
                <div className="space-y-6 md:space-y-8">
                    {/* Profile card skeleton */}
                    <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
                        <div className="flex items-center justify-between mb-4 md:mb-6 animate-pulse">
                            <div>
                                <div className="h-6 md:h-7 bg-slate-200 rounded w-20 md:w-24 mb-2"></div>
                                <div className="h-3 md:h-4 bg-slate-200 rounded w-40 md:w-48"></div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            <div className="space-y-2 animate-pulse">
                                <div className="h-3 md:h-4 bg-slate-200 rounded w-24 md:w-28"></div>
                                <div className="h-10 md:h-12 bg-slate-100 rounded-xl w-full"></div>
                            </div>
                            <div className="space-y-2 animate-pulse">
                                <div className="h-3 md:h-4 bg-slate-200 rounded w-20 md:w-24"></div>
                                <div className="h-10 md:h-12 bg-slate-100 rounded-xl w-full"></div>
                            </div>
                        </div>
                        <div className="flex justify-end pt-4 md:pt-6 animate-pulse">
                            <div className="h-9 md:h-10 bg-slate-200 rounded-xl w-32 md:w-36"></div>
                        </div>
                    </div>

                    {/* Delete account card skeleton */}
                    <div className="bg-white rounded-2xl p-6 md:p-8 border border-red-100 shadow-sm">
                        <div className="flex flex-col sm:flex-row items-start justify-between gap-4 md:gap-6 animate-pulse">
                            <div className="flex-1">
                                <div className="h-5 md:h-6 bg-red-100 rounded w-28 md:w-32 mb-2 md:mb-3"></div>
                                <div className="h-3 md:h-4 bg-slate-200 rounded w-full max-w-lg"></div>
                            </div>
                            <div className="h-10 md:h-11 bg-red-100 rounded-xl w-full sm:w-36 md:w-40"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export const SettingsPage: React.FC = () => {
    const { user, loading } = useAuth();

    // Show skeleton with sidebar layout during auth loading
    if (loading) {
        return <SettingsSkeleton />;
    }

    // Route to appropriate settings page based on user type
    const userType = user?.user_metadata?.user_type;

    // Check if user is a hirer (company or recruiter)
    if (userType === 'company' || userType === 'hirer') {
        return <CompanySettingsPage />;
    }

    // Default to candidate settings
    return <CandidateSettingsPage />;
};
